import { Fragment, useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, Checkbox, FormControlLabel, IconButton,
    Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const PLANS = [
    { id: 'my_health_singapore_elite',     key: 'elite',     label: 'Elite',     hsKey: 'Elite' },
    { id: 'my_health_singapore_extensive', key: 'extensive', label: 'Extensive', hsKey: 'Extensive' },
    { id: 'my_health_singapore_essential', key: 'essential', label: 'Essential', hsKey: 'Essential' },
];

const OPTIONAL_COVERS = [
    { key: 'op', label: 'Outpatient', rateKey: 'op', premiumField: 'op_premium' },
    { key: 'ma', label: 'Maternity',  rateKey: 'ma', premiumField: 'ma_premium' },
    { key: 'dn', label: 'Dental',     rateKey: 'dn', premiumField: 'dn_premium' },
];

// Same as Quotation1 — MyHealthSingapore_Rates only offers Annually
const PAYMENT_FREQUENCIES = ['Annually'];
const WELCOME_DISCOUNT_RATE = 0.10;
// Welcome Discount checkbox is hidden for now — set to true to show it again
const SHOW_WELCOME_DISCOUNT = false;
const BRAND_COLOR = '#004161';
const BRAND_HOVER = '#003049';
const BRAND_ACCENT = '#C9D213';
const BRAND_SUBTITLE = '#6a9a4a';
const BRAND_ACCENT_TEXT = '#002047';
const DEFAULT_COVER_PLANS = { op: 'N/A', ma: 'N/A', dn: 'N/A' };

// Legacy plans stored a single optional_covers list rather than per-component plans;
// they always used the base HS plan.
function legacyCoverPlanKey(basePlanId) {
    const planObj = PLANS.find(p => p.id === basePlanId) || PLANS[0];
    return planObj.hsKey;
}

function formatRate(rate) {
    if (rate === null || rate === undefined || rate === '') return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return 'S$' + rate.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function MyHealthSingapore_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const sgConfig           = appConfig?.content?.specifics?.my_health_singapore || {};
    const areaOptions        = appConfig?.content?.set2_area_of_coverage || [];
    const defaultArea        = areaOptions.includes('Worldwide excl USA') ? 'Worldwide excl USA' : (areaOptions[0] || '');
    const deductibleOptions  = appConfig?.content?.deductibles?.my_health_singapore?.['SGD'] || [];
    const coInsOptions       = Object.keys(sgConfig?.co_ins || {});
    const ipNetworkOptions   = sgConfig?.ip_network || [];
    const appTypeOptions     = Object.keys(sgConfig?.application_type || {});
    const gstRate            = sgConfig?.gst_rate || 0;
    const lowClaimOptions    = Object.keys(sgConfig?.low_claim_discount || {});

    const [selectedPlan, setSelectedPlan]         = useState('my_health_singapore_elite');
    // { op: planName|'N/A', ma: planName|'N/A', dn: planName|'N/A' }
    const [coverPlans, setCoverPlans]             = useState(DEFAULT_COVER_PLANS);
    const [ipNetwork, setIpNetwork]               = useState('');
    const [applicationType, setApplicationType]   = useState('');
    const [areaOfCoverage, setAreaOfCoverage]     = useState(defaultArea);
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [welcomeDiscount, setWelcomeDiscount]   = useState(false);
    const [lowClaimDiscount, setLowClaimDiscount] = useState(lowClaimOptions[0] || '');
    // { [quoteName]: { deductible: '', co_insurance: '' } }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    // Set(clientUid) — which eligible females get maternity, when 2+ are eligible. Applies to all quotes.
    const [maternityClients, setMaternityClients] = useState(null);
    const [ratesData, setRatesData]               = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    const [loading, setLoading]                   = useState(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const initializingFromEdit = useRef(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedPlanObj   = PLANS.find(p => p.id === selectedPlan) || PLANS[0];
    const planOptions        = sgConfig?.plans || [];
    const maPlanOptions      = planOptions.filter(p => p !== 'Core');
    const coverPlanOptions   = { op: planOptions, ma: maPlanOptions, dn: planOptions };
    const activeCovers       = OPTIONAL_COVERS.filter(c => coverPlans[c.key] && coverPlans[c.key] !== 'N/A');
    const familyDiscount     = sgConfig?.family_discount;
    const eligibleFemales    = clients.filter(isMaternityEligible);
    // Application Type is a client-side multiplier — does not require a new API call
    const appTypeRate        = sgConfig?.application_type?.[applicationType] || 0;
    const appTypeMultiplier  = 1 + appTypeRate;
    // Low Claim Discount (for renewals) is a client-side discount too — same as MyHealthSingapore_Rates' getRate
    const lowClaimRate       = sgConfig?.low_claim_discount?.[lowClaimDiscount] || 0;
    const lowClaimMultiplier = 1 - lowClaimRate;
    const areaOfCover        = sgConfig?.area_of_cover?.[areaOfCoverage] || '';

    // The backend bakes the family (community) discount into the fetched rate. The Welcome
    // Discount is layered on top afterwards (per client) rather than replacing other discounts/loadings.
    function getEffectiveRate(quoteUid, clientIndex, rateKey) {
        const raw = ratesData?.[quoteUid]?.rawRates?.[clientIndex];
        if (!raw) return null;

        const rate = raw[rateKey];
        if (typeof rate !== 'number') return rate ?? null;

        return rate * lowClaimMultiplier * appTypeMultiplier;
    }

    // When 2+ clients are eligible for maternity, the user picks who it applies to (applies to all quotes).
    // With 0 or 1 eligible clients there's nothing to choose, so it applies automatically.
    function isMaternitySelectedForClient(client) {
        if (!isMaternityEligible(client)) return false;
        if (eligibleFemales.length < 2) return true;
        return maternityClients ? maternityClients.has(client.uid) : true;
    }

    function toggleMaternityClient(clientUid) {
        setMaternityClients(prev => {
            const current = prev ? new Set(prev) : new Set(eligibleFemales.map(c => c.uid));
            if (current.has(clientUid)) current.delete(clientUid);
            else current.add(clientUid);
            return current;
        });
        invalidateRates();
    }

    function getFamilyDiscountLabel() {
        const clientsCount = clients.length;
        if (!familyDiscount) return '0%';
        const discount = clientsCount > 7
            ? (familyDiscount['>7'] || 0)
            : (familyDiscount[clientsCount] || 0);
        return `${discount * 100}%`;
    }

    // Maternity is only available with a Nil deductible, Elite/Extensive HS, and an Outpatient plan.
    // (Per-client age/gender eligibility is handled automatically — ineligible clients get 'N/A' plan.)
    function getValidationMessages() {
        const messages = [];
        if (coverPlans.ma === 'N/A') return messages;

        const hsEligible       = ['Elite', 'Extensive'].includes(selectedPlanObj.hsKey);
        const opSelected       = coverPlans.op !== 'N/A';
        const allNilDeductible = (quotes || []).filter(q => !isBlankQuote(q)).every(q => (quoteDeductibles[q.name]?.deductible || '') === 'Nil');

        if (!hsEligible || !opSelected || !allNilDeductible) {
            messages.push('Maternity is only available if Nil deductible, Elite or Extensive Hospital and Surgery, and an Outpatient plan have been selected. Please change your selection.');
        }

        return messages;
    }

    const validationMessages = getValidationMessages();

    function getDefaultQuoteDeductibles() {
        const defaults = {};
        (quotes || []).forEach(q => {
            defaults[q.name] = {
                deductible:   deductibleOptions[0] || '',
                co_insurance: coInsOptions[0]       || '',
            };
        });
        return defaults;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            initializingFromEdit.current = true;
            const basePlanId = editingPlan.base_plan_id || editingPlan.plan_id;
            setSelectedPlan(basePlanId);

            const legacyCovers = new Set(editingPlan.payloads?.optional_covers || []);
            const legacyPlan   = legacyCoverPlanKey(basePlanId);
            setCoverPlans({
                op: editingPlan.payloads?.op_plan ?? (legacyCovers.has('op') ? legacyPlan : 'N/A'),
                ma: editingPlan.payloads?.ma_plan ?? (legacyCovers.has('ma') ? legacyPlan : 'N/A'),
                dn: editingPlan.payloads?.dn_plan ?? (legacyCovers.has('dn') ? legacyPlan : 'N/A'),
            });
            setIpNetwork(editingPlan.payloads?.ip_network || ipNetworkOptions[0] || '');
            setApplicationType(editingPlan.payloads?.application_type || appTypeOptions[0] || '');
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setWelcomeDiscount(!!editingPlan.payloads?.welcome_discount);
            setLowClaimDiscount(editingPlan.payloads?.low_claim_discount || lowClaimOptions[0] || '');

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid]
                    || { deductible: '', co_insurance: '' };
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData (and which clients had maternity applied) from stored premiums
            const ratesMap = {};
            const maternitySelections = new Set();
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    const maPremium = premium?.ma_premium ?? null;
                    if (maPremium !== null && maPremium !== 'N/A') maternitySelections.add(client.uid);
                    return {
                        hs: premium?.hs_premium ?? null,
                        op: premium?.op_premium ?? null,
                        ma: maPremium,
                        dn: premium?.dn_premium ?? null,
                        family_discount_rate: premium?.family_discount_rate || 0,
                    };
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
            setMaternityClients(maternitySelections.size > 0 ? maternitySelections : null);
            setTimeout(() => { initializingFromEdit.current = false; }, 0);
        } else if (open && !editingPlan) {
            setIpNetwork(ipNetworkOptions[0] || '');
            setApplicationType(appTypeOptions[0] || '');
            setAreaOfCoverage(defaultArea);
            setPaymentFrequency('Annually');
            setWelcomeDiscount(false);
            setLowClaimDiscount(lowClaimOptions[0] || '');
            setCoverPlans(DEFAULT_COVER_PLANS);
            setQuoteDeductibles(getDefaultQuoteDeductibles());
            setMaternityClients(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Ensure co-insurance has a default value even if hidden, to prevent validation errors
    useEffect(() => {
        if (open && coInsOptions.length > 0) {
            setQuoteDeductibles(prev => {
                let changed = false;
                const next = { ...prev };
                (quotes || []).forEach(q => {
                    if (!next[q.name]?.co_insurance) {
                        next[q.name] = { ...(next[q.name] || {}), co_insurance: coInsOptions[0] };
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
        }
    }, [open, coInsOptions, quotes]);

    // Clear rates when plan changes
    useEffect(() => {
        if (initializingFromEdit.current) return;
        invalidateRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPlan]);

    function setCoverPlan(key, value) {
        setCoverPlans(prev => ({ ...prev, [key]: value }));
        invalidateRates();
    }

    function setQuoteField(quoteName, field, value) {
        setQuoteDeductibles(prev => ({
            ...prev,
            [quoteName]: { ...(prev[quoteName] || {}), [field]: value },
        }));
        setDeductibleErrors(prev => ({
            ...prev,
            [quoteName]: { ...(prev[quoteName] || {}), [field]: false },
        }));
        invalidateRates();
    }

    function isMaternityEligible(client) {
        return client.gender === 'Female' && Number(client.age) >= 19 && Number(client.age) <= 45;
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name]?.deductible === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (isBlankQuote(q)) return;
            const d = quoteDeductibles[q.name];
            const qErrors = {};
            if (!d?.deductible)   qErrors.deductible   = true;
            // Only validate co-insurance if Outpatient (op) is selected
            if (coverPlans.op !== 'N/A' && !d?.co_insurance) qErrors.co_insurance = true;
            if (Object.keys(qErrors).length > 0) errors[q.name] = qErrors;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please complete deductibles for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { my_health_singapore: quoteDeductibles[quote.name]?.deductible || '' },
                            insurers: ['my_health_singapore'],
                            specifics: {
                                my_health_singapore: clients.map((client) => ({
                                    hs: {
                                        plan:       selectedPlanObj.hsKey,
                                        ip_network: ipNetwork,
                                        deductible: quoteDeductibles[quote.name]?.deductible || '',
                                    },
                                    op: {
                                        plan:   coverPlans.op,
                                        co_ins: quoteDeductibles[quote.name]?.co_insurance || (coInsOptions[0] || ''),
                                    },
                                    ma: {
                                        plan: coverPlans.ma !== 'N/A' && isMaternitySelectedForClient(client) ? coverPlans.ma : 'N/A',
                                    },
                                    dn: {
                                        plan: coverPlans.dn,
                                    },
                                    application_type: applicationType,
                                })),
                            },
                            specific_area_of_coverage:  { my_health_singapore: areaOfCoverage },
                            rates_version_per_insurer:  { my_health_singapore: selectedRatesVersion?.my_health_singapore || '' },
                            specific_currency:          { my_health_singapore: 'SGD' },
                            specific_payment_frequency: { my_health_singapore: paymentFrequency },
                        },
                    }).then(response => ({ quote, data: response?.data?.my_health_singapore }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quote, data, blank }) => {
                if (blank) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map((r, idx) => ({
                        hs: r.hs ?? null,
                        op: r.op ?? null,
                        ma: coverPlans.ma !== 'N/A' && !isMaternitySelectedForClient(clients[idx]) ? 'N/A' : (r.ma ?? null),
                        dn: r.dn ?? null,
                        family_discount_rate: r.family_discount_rate || 0,
                    }));
                    ratesMap[quote.uid] = { rawRates };
                } else {
                    ratesMap[quote.uid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching MyHealth Singapore rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    // Client subtotal = HS + active optional covers (pre Welcome Discount)
    function getClientSubtotal(clientIndex, quoteUid) {
        const raw = ratesData?.[quoteUid]?.rawRates?.[clientIndex];
        if (!raw) return null;
        let total = 0;
        let hasAny = false;
        const hsRate = getEffectiveRate(quoteUid, clientIndex, 'hs');
        if (typeof hsRate === 'number') { total += hsRate; hasAny = true; }
        for (const cover of activeCovers) {
            const r = getEffectiveRate(quoteUid, clientIndex, cover.rateKey);
            if (typeof r === 'number') { total += r; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Per-client Welcome Discount amount, computed off that client's own subtotal
    // (family/community discount and application type loading remain intact)
    function getClientWelcomeDiscountAmount(clientIndex, quoteUid) {
        if (!welcomeDiscount) return 0;
        const subtotal = getClientSubtotal(clientIndex, quoteUid);
        return typeof subtotal === 'number' ? subtotal * WELCOME_DISCOUNT_RATE : 0;
    }

    // Client subtotal after the per-client Welcome Discount, if active
    function getClientDiscountedSubtotal(clientIndex, quoteUid) {
        const subtotal = getClientSubtotal(clientIndex, quoteUid);
        if (subtotal === null) return null;
        return subtotal - getClientWelcomeDiscountAmount(clientIndex, quoteUid);
    }

    // Column subtotal (sum across all clients for a given rate key), pre Welcome Discount
    function getColumnSubtotal(rateKey, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getEffectiveRate(quoteUid, i, rateKey);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Total premium for the quote (sum of all client subtotals), pre Welcome Discount
    function getQuoteSubtotal(quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const sub = getClientSubtotal(i, quoteUid);
            if (typeof sub === 'number') { total += sub; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Total Welcome Discount for the quote (sum of each client's own discount amount)
    function getQuoteWelcomeDiscountAmount(quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const amount = getClientWelcomeDiscountAmount(i, quoteUid);
            if (typeof amount === 'number') { total += amount; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Total premium for the quote after the per-client Welcome Discount, if active
    function getQuoteDiscountedSubtotal(quoteUid) {
        const subtotal = getQuoteSubtotal(quoteUid);
        if (subtotal === null) return null;
        return subtotal - (getQuoteWelcomeDiscountAmount(quoteUid) || 0);
    }

    function computeGST(totalPremium) {
        if (totalPremium === null) return null;
        return totalPremium * gstRate;
    }

    function buildPlanLabel() {
        let label = `HS ${selectedPlanObj.label}`;
        if (ipNetwork) label += ` (${ipNetwork})`;
        if (coverPlans.op !== 'N/A') label += ` + OP ${coverPlans.op}`;
        if (coverPlans.ma !== 'N/A') label += ` + MA ${coverPlans.ma}`;
        if (coverPlans.dn !== 'N/A') label += ` + DN ${coverPlans.dn}`;
        return label;
    }

    function buildDeductibleString(quoteName) {
        const d = quoteDeductibles[quoteName];
        return d?.deductible || '';
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const coversSuffix = OPTIONAL_COVERS
            .filter(c => coverPlans[c.key] !== 'N/A')
            .map(c => `${c.key}_${coverPlans[c.key]}`)
            .join('_')
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, '');
        const effectivePlanId = editingPlan
            ? editingPlan.plan_id
            : (coversSuffix ? `${selectedPlan}_${coversSuffix}` : selectedPlan);
        // plan_id identifies the tier + covers combination and is shared by every instance of it;
        // uid identifies this specific plan instance so multiple plans of the same combination
        // can coexist instead of overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            const totalPremium = getQuoteDiscountedSubtotal(quote.uid);
            const totalTax     = computeGST(totalPremium);
            const totalWithTax = totalPremium !== null && totalTax !== null ? totalPremium + totalTax : null;

            clients.forEach((client, clientIndex) => {
                const rawRates       = ratesData[quote.uid]?.rawRates?.[clientIndex];
                const clientSubtotal = getClientDiscountedSubtotal(clientIndex, quote.uid);
                // GST is a flat percentage — apply per client
                const rateWithTax    = clientSubtotal !== null ? clientSubtotal * (1 + gstRate) : null;
                updates.push({
                    quoteUid:  quote.uid,
                    clientUid: client.uid,
                    planId:    effectivePlanId,
                    planUid,
                    premium: {
                        insurer:           'my_health_singapore',
                        plan:              selectedPlanObj.key,
                        plan_label:        planLabel,
                        included_benefits: '',
                        deductible:        buildDeductibleString(quote.name),
                        co_insurance:      quoteDeductibles[quote.name]?.co_insurance || '',
                        area_of_coverage:  areaOfCoverage,
                        currency:          'SGD',
                        payment_frequency: paymentFrequency,
                        optional_covers:   activeCovers.map(c => c.key),
                        op_plan:           coverPlans.op,
                        ma_plan:           coverPlans.ma,
                        dn_plan:           coverPlans.dn,
                        premium_rate:      rateWithTax ?? null,
                        hs_premium:        rawRates?.hs ?? null,
                        op_premium:        rawRates?.op ?? null,
                        ma_premium:        rawRates?.ma ?? null,
                        dn_premium:        rawRates?.dn ?? null,
                        family_discount_rate: rawRates?.family_discount_rate ?? null,
                        total_premium:     totalPremium  ?? null,
                        total_tax:         totalTax      ?? null,
                        total_with_tax:    totalWithTax,
                    },
                });
            });
        });

        const plan = {
            plan_id:      effectivePlanId,
            uid:          planUid,
            base_plan_id: selectedPlan,
            plan:         selectedPlanObj.key,
            insurer:      'my_health_singapore',
            label:    `April (MyHEALTH) Singapore ${planLabel}`,
            payloads: {
                area_of_coverage:  areaOfCoverage,
                currency:          'SGD',
                payment_frequency: paymentFrequency,
                optional_covers:   activeCovers.map(c => c.key),
                op_plan:           coverPlans.op,
                ma_plan:           coverPlans.ma,
                dn_plan:           coverPlans.dn,
                ip_network:        ipNetwork,
                application_type:  applicationType,
                welcome_discount:  welcomeDiscount,
                low_claim_discount: lowClaimDiscount,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || {}])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    // Plans saved before the list matched Quotation1 can hold another frequency — keep
    // showing that saved value instead of an empty select
    const paymentFrequencyOptions = PAYMENT_FREQUENCIES.includes(paymentFrequency) ? PAYMENT_FREQUENCIES : [...PAYMENT_FREQUENCIES, paymentFrequency];

    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    height: 60,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.2,
                    fontWeight: 600,
                }}><span style={{ color: '#fff' }}>MyHEALTH</span> <span style={{ color: BRAND_SUBTITLE }}>Singapore</span></div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={`${process.env.PUBLIC_URL}/images/Sompo Logo.png`} alt="Sompo" style={{ height: 50, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <img src={`${process.env.PUBLIC_URL}/images/April International Logo.png`} alt="April International" style={{ height: 50, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type — Hospital & Surgery */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center' }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Hospital & Surgery</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={selectedPlan}
                                disabled={loading || !!editingPlan}
                                // Only the selected plans are priced, so a new plan needs new rates
                                onChange={(e) => { setSelectedPlan(e.target.value); invalidateRates(); }}
                                sx={selectSx}
                            >
                                {PLANS.map(plan => (
                                    <MenuItem key={plan.id} value={plan.id} sx={menuItemSx}>{plan.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {ipNetworkOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>IP Network</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    <Select value={ipNetwork} disabled={loading} onChange={(e) => { setIpNetwork(e.target.value); invalidateRates(); }} sx={selectSx}>
                                        {ipNetworkOptions.map(opt => <MenuItem key={opt} value={opt} sx={menuItemSx}>{opt}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Optional covers — Outpatient / Maternity / Dental */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Optional Covers</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center' }}>
                        {OPTIONAL_COVERS.map(cover => (
                            <Fragment key={cover.key}>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>{cover.label}</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    <Select
                                        value={coverPlans[cover.key]}
                                        disabled={loading || !!editingPlan}
                                        onChange={(e) => setCoverPlan(cover.key, e.target.value)}
                                        sx={selectSx}
                                    >
                                        <MenuItem value="N/A" sx={menuItemSx}>N/A</MenuItem>
                                        {coverPlanOptions[cover.key].map(p => (
                                            <MenuItem key={p} value={p} sx={menuItemSx}>{p}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Fragment>
                        ))}
                    </Box>
                    {coverPlans.ma !== 'N/A' && eligibleFemales.length >= 2 && (
                        <Box sx={{ mt: 1.5, maxWidth: 320 }}>
                            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Maternity for</Typography>
                            {eligibleFemales.map(client => (
                                <FormControlLabel
                                    key={client.uid}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={isMaternitySelectedForClient(client)}
                                            onChange={() => toggleMaternityClient(client.uid)}
                                            disabled={loading}
                                            sx={{ p: 0.5 }}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: 13 }}>{client.full_name || 'Client'}</Typography>}
                                    sx={{ mr: 1.5 }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Options — same as MyHealthSingapore_Rates */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px auto', columnGap: 2, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        {areaOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area Of Coverage</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    <Select value={areaOfCoverage} disabled={loading} onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }} sx={selectSx}>
                                        {areaOptions.map(area => <MenuItem key={area} value={area} sx={menuItemSx}>{area}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Box />
                            </>
                        )}

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value="SGD" disabled sx={selectSx}>
                                <MenuItem value="SGD" sx={menuItemSx}>SGD</MenuItem>
                            </Select>
                        </FormControl>
                        <Box />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={paymentFrequency} disabled={loading} onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {paymentFrequencyOptions.map(f => <MenuItem key={f} value={f} sx={menuItemSx}>{f}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Box />

                        {lowClaimOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Low Claim Discount (for renewals)</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    {/* Applied to the fetched rates on the frontend — no refetch */}
                                    <Select value={lowClaimDiscount} disabled={loading} onChange={(e) => setLowClaimDiscount(e.target.value)} sx={selectSx}>
                                        {lowClaimOptions.map(opt => <MenuItem key={opt} value={opt} sx={menuItemSx}>{opt}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Typography sx={{ fontSize: 13 }}>{lowClaimRate ? `${(lowClaimRate * 100).toFixed(0)}%` : ''}</Typography>
                            </>
                        )}

                        {appTypeOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Application Type</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    {/* Applied to the fetched rates on the frontend — no refetch */}
                                    <Select value={applicationType} disabled={loading} onChange={(e) => setApplicationType(e.target.value)} sx={selectSx}>
                                        {appTypeOptions.map(opt => <MenuItem key={opt} value={opt} sx={menuItemSx}>{opt}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Typography sx={{ fontSize: 13 }}>{appTypeRate ? `${(appTypeRate * 100).toFixed(0)}%` : ''}</Typography>
                            </>
                        )}

                        {/* Old plans saved with Welcome Discount checked still show it so it can be unchecked */}
                        {(SHOW_WELCOME_DISCOUNT || !!editingPlan?.payloads?.welcome_discount) && (
                            <FormControlLabel
                                sx={{ gridColumn: '1 / -1', mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={welcomeDiscount}
                                        onChange={(e) => setWelcomeDiscount(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Welcome Discount</Typography>}
                            />
                        )}

                        <Typography sx={{ gridColumn: '1 / -1', fontSize: 13 }}>
                            Community Discount: <span style={{ color: BRAND_ACCENT_TEXT, fontWeight: 600 }}>{getFamilyDiscountLabel()}</span>
                            {areaOfCover && <>&nbsp;&nbsp;|&nbsp;&nbsp;Area of Coverage: <span style={{ color: BRAND_ACCENT_TEXT, fontWeight: 600 }}>{areaOfCover}</span></>}
                        </Typography>
                </Box>
                </Box>

                {validationMessages.length > 0 && (
                    <Box component="ul" sx={{ m: 0, mb: 2, pl: 3, color: '#d32f2f' }}>
                        {validationMessages.map((message, idx) => (
                            <Typography component="li" key={idx} sx={{ fontSize: 13 }}>{message}</Typography>
                        ))}
                    </Box>
                )}

                <Divider sx={{ mb: 2 }} />

                {/* Deductibles per Quote */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{quote.name}</Typography>
                                <Box sx={{ display: 'flex', gap: 2, pl: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: 70 }}>Deductible</Typography>
                                        <CustomSelect2
                                            dense
                                            value={quoteDeductibles[quote.name]?.deductible || ''}
                                            onChange={(e) => setQuoteField(quote.name, 'deductible', e.target.value)}
                                            items={[LEAVE_BLANK, ...deductibleOptions]}
                                            disabled={loading}
                                            error={!!deductibleErrors[quote.name]?.deductible}
                                            sx={{ minWidth: 200 }}
                                        />
                                    </Box>
                                    {coInsOptions.length > 0 && coverPlans.op !== 'N/A' && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: 55 }}>Co-ins.</Typography>
                                            <CustomSelect2
                                                dense
                                                value={quoteDeductibles[quote.name]?.co_insurance || ''}
                                                onChange={(e) => setQuoteField(quote.name, 'co_insurance', e.target.value)}
                                                items={coInsOptions}
                                                disabled={loading || isBlankQuote(quote)}
                                                error={!!deductibleErrors[quote.name]?.co_insurance}
                                                isZeroDeductibleUniform={false}
                                                sx={{ minWidth: 130 }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: BRAND_COLOR, textTransform: 'none', '&:hover': { backgroundColor: BRAND_HOVER }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {/* Loading */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
                    </Box>
                )}

                {/* Rates Display */}
                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => {
                            const d = quoteDeductibles[quote.name];
                            const summary = isBlankQuote(quote) ? LEAVE_BLANK : [d?.deductible, (coverPlans.op !== 'N/A' && d?.co_insurance) ? `Co-ins: ${d.co_insurance}` : ''].filter(Boolean).join(' | ');
                            const totalPremium      = getQuoteSubtotal(quote.uid);
                            const discountedSubtotal = getQuoteDiscountedSubtotal(quote.uid);
                            const totalGST     = computeGST(discountedSubtotal);
                            const totalWithTax = discountedSubtotal !== null && totalGST !== null ? discountedSubtotal + totalGST : null;

                            return (
                                <Box key={quote.name} sx={{ mb: 3 }}>
                                    <Typography fontWeight={600} sx={{ mb: 1 }}>
                                        {quote.name}
                                        <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                            — {summary}
                                        </Typography>
                                    </Typography>
                                    {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 180 }}>Module</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                            <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                            <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 110 }}>Subtotal</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {/* HS row */}
                                                <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>Hospital & Surgery ({selectedPlanObj.label})</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getEffectiveRate(quote.uid, idx, 'hs'))}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(getColumnSubtotal('hs', quote.uid))}
                                                    </TableCell>
                                                </TableRow>
                                                {/* Optional cover rows */}
                                                {activeCovers.map((cover, coverIdx) => (
                                                    <TableRow key={cover.key} sx={{ backgroundColor: (coverIdx + 1) % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                        <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{cover.label} ({coverPlans[cover.key]})</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx}>
                                                                {formatRate(getEffectiveRate(quote.uid, idx, cover.rateKey))}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 500 }}>
                                                            {formatRate(getColumnSubtotal(cover.rateKey, quote.uid))}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {/* Welcome Discount row */}
                                                {welcomeDiscount && (
                                                    <TableRow sx={{ backgroundColor: '#fff' }}>
                                                        <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                            Welcome Discount ({(WELCOME_DISCOUNT_RATE * 100).toFixed(0)}%)
                                                        </TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx}>
                                                                {formatRate(getClientWelcomeDiscountAmount(idx, quote.uid))}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 500 }}>
                                                            {formatRate(getQuoteWelcomeDiscountAmount(quote.uid))}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {/* Client subtotal row */}
                                                <TableRow sx={{ backgroundColor: '#e8f4f8' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>Annual Premium (SGD)</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                            {formatRate(getClientDiscountedSubtotal(idx, quote.uid))}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        {formatRate(discountedSubtotal)}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Paper>
)}

                                    {/* GST breakdown */}
                                    {totalPremium !== null && (
                                        <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: BRAND_ACCENT, borderRadius: 1, display: 'inline-flex', flexDirection: 'column', gap: 0.5, minWidth: 300 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                                                <Typography sx={{ fontSize: 13, color: BRAND_COLOR, fontWeight: 500 }}>Annual Premium</Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: BRAND_COLOR }}>{formatRate(discountedSubtotal)}</Typography>
                                            </Box>
                                            {gstRate > 0 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                                                    <Typography sx={{ fontSize: 13, color: BRAND_COLOR, fontWeight: 500 }}>
                                                        GST ({(gstRate * 100).toFixed(0)}%)
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: BRAND_COLOR }}>{formatRate(totalGST)}</Typography>
                                                </Box>
                                            )}
                                            <Divider sx={{ borderColor: BRAND_COLOR, opacity: 0.3 }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                                                <Typography sx={{ fontSize: 13, color: BRAND_COLOR, fontWeight: 700 }}>Annual Premium incl. GST</Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: BRAND_COLOR }}>{formatRate(totalWithTax)}</Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="my_health_singapore" />

                <RatesVersionSelect
                    insurer="my_health_singapore"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />
                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: BRAND_COLOR, textTransform: 'none', '&:hover': { backgroundColor: BRAND_HOVER }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
